import React, { useCallback, useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tabs,
  Tab,
  Typography,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { v4 as uuidv4 } from "uuid"; // for uuid version 4
import { getListOfAccts, postAwsAcc, getAwsAcc } from "../../store/actions";
import {
  getReleaseDetailsRequest,
  clearAllReleaseMessages,
} from "../../store/actions";
import { SnackbarProvider, enqueueSnackbar } from "notistack";
import { clearAllScanServiceMessages } from "../../store/scanService/scanServiceSlice";
import AdditionalAddons from "./AddonsManagement/AdditionalAddons/AdditionalAddons";
import DocumentIcon from "@mui/icons-material/Description";
import OutboundIcon from "@mui/icons-material/Outbound";
import RemediationPreviewPanel from "../Common/RemediationPreviewPanel";

const PAIRING_INTRO =
  "Before you begin, ensure you are logged into the AWS account onboarded, in the appropriate region, and have full privileges for the account.";

const PAIRING_CLOSING =
  "Once complete, navigate back to CloudCatcher and click Update Role and External ID to verify.";

const PAIRING_GUIDES = [
  {
    id: "create",
    title: "Create a new IAM role",
    body: "Select the Account Pairing launch button or copy the pairing link URL and paste into a new tab to complete setting up a new IAM role through the IAM role creation console. Once complete, copy the name of the role and paste into the IAM Role bar. Click Update Role and External ID to verify.",
  },
  {
    id: "update",
    title: "Update current IAM role",
    body: "To update the current IAM role, navigate to IAM of the AWS account and search the current IAM Role name stated in the IAM Role bar below. Select the role > navigate to Trust Relationships > Edit Trust Policy > Copy the External ID below and paste into the line stating the External ID > save by selecting Update Trust Policy.",
  },
];

const RESOURCE_EXPLORER_URL =
  "https://resource-explorer.console.aws.amazon.com/resource-explorer/home?region=us-east-1#/home";

const accordionSx = {
  boxShadow: "none",
  border: "1px solid #e2e8f0",
  borderRadius: "8px !important",
  backgroundColor: "#fff",
  "&:before": { display: "none" },
  "&.Mui-expanded": {
    margin: 0,
  },
};

const accordionSummarySx = {
  minHeight: 40,
  px: 1.5,
  "&.Mui-expanded": { minHeight: 40 },
  "& .MuiAccordionSummary-content": {
    my: 1,
    "&.Mui-expanded": { my: 1 },
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "#64748b",
  },
};

const UpdateRoleInfoComp = (props) => {
  const {
    handleUpdateIamRole,
    newRoleName,
    setNewRoleName,
    newExternalId,
    setNewExternalId,
  } = props;
  console.log("UpdateRoleInfoComp, props", props.newExternalId);
  const [isCopied, setIsCopied] = React.useState(false);
  const [expandedGuides, setExpandedGuides] = useState({
    create: false,
    update: false,
  });

  const linkTextAreaRef = useRef(null);
  const [localGuidePreviewUrl, setLocalGuidePreviewUrl] = useState(null);

  const getPairingConsoleUrl = () =>
    `https://console.aws.amazon.com/iam/home#/roles$new?step=review&roleType=crossAccount&accountID=${props.aws}&policies=arn%3Aaws%3Aiam%3A%3Aaws%3Apolicy%2FReadOnlyAccess,arn%3Aaws%3Aiam%3A%3Aaws%3Apolicy%2FWellArchitectedConsoleFullAccess&roleName=${newRoleName}&externalId=${newExternalId}&trustedEntityType=AWS_ACCOUNT&isThirdParty=true&awsAccount=${props.aws}`;

  const handleManualPairingGuide = async (e) => {
    e.stopPropagation();
    props.getReleaseDetails(
      encodeURIComponent(
        "configurations/Self-Onboarding-Manual-Guide-to-Pair-New-Account.pdf"
      )
    );
  };

  const handleResourceExplorerGuide = async (e) => {
    e.stopPropagation();
    props.getReleaseDetails(
      process.env.REACT_APP_RESOURCE_EXPLORER_MANUAL_GUIDE
    );
  };

  const handleClicktoCopy = () => {
    const url = getPairingConsoleUrl();
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  };

  useEffect(() => {
    if (props.aws === null) {
      props.getAwsAcc();
    }
  }, []);

  const clearAllReleaseMessages = useCallback(() =>
    props.clearAllReleaseMessages()
  );
  const releaseDetails = props.releaseDetails;
  useEffect(() => {
    if (releaseDetails !== null) {
      if (props.onOpenGuidePreview) {
        props.onOpenGuidePreview(releaseDetails);
      } else {
        setLocalGuidePreviewUrl(releaseDetails);
      }
      clearAllReleaseMessages();
    }
  }, [clearAllReleaseMessages, releaseDetails]);

  const handleRefresh = () => {
    const newUuid = uuidv4();
    setNewExternalId(newUuid);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newExternalId);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const isInvalid = () => {
    const isInvalidItems = [
      {
        field: "newRoleName",
        isInvalid:
          newRoleName === "" ||
          newRoleName === null ||
          newRoleName === undefined
            ? true
            : false,
      },
      {
        field: "newExternalId",
        isInvalid:
          newExternalId === "" ||
          newExternalId === null ||
          newExternalId === undefined
            ? true
            : false,
      },
    ];
    const resp = isInvalidItems.filter((item) => item.isInvalid);
    return resp.length > 0;
  };

  const actionLinkSx = {
    textTransform: "none",
    color: "#2563eb",
    border: "none",
    backgroundColor: "transparent",
    fontWeight: 500,
    fontSize: "0.875rem",
    borderRadius: "6px",
    px: 0.5,
    py: 0.5,
    boxShadow: "none",
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      border: "none",
    },
  };

  const guideBtnSx = {
    textTransform: "none",
    color: "#2563eb",
    border: "1px solid #93c5fd",
    backgroundColor: "#fff",
    fontWeight: 500,
    fontSize: "0.8125rem",
    borderRadius: "999px",
    px: 1.5,
    py: 0.25,
    boxShadow: "none",
    minHeight: 32,
    "&:hover": {
      backgroundColor: "#eff6ff",
      border: "1px solid #93c5fd",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          p: 2.5,
          backgroundColor: "#f3f4f6",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            pb: 1.25,
            mb: 1.25,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography
            sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}
          >
            Update Pairing
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 1.25,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: 1.6,
              width: "100%",
            }}
          >
            {PAIRING_INTRO}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 1.25,
              width: "100%",
              alignItems: "start",
            }}
          >
            {PAIRING_GUIDES.map((guide) => (
              <Accordion
                key={guide.id}
                disableGutters
                expanded={Boolean(expandedGuides[guide.id])}
                onChange={(_, isExpanded) =>
                  setExpandedGuides((prev) => ({
                    ...prev,
                    [guide.id]: isExpanded,
                  }))
                }
                sx={accordionSx}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                  sx={accordionSummarySx}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "#0f172a",
                      lineHeight: 1.35,
                    }}
                  >
                    {guide.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: "0.8125rem",
                      fontWeight: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {guide.body}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: 1.6,
              width: "100%",
            }}
          >
            {PAIRING_CLOSING}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr max-content",
            columnGap: 2,
            rowGap: 1.25,
            alignItems: "center",
            mb: 1.25,
          }}
        >
          <Button
            variant="text"
            sx={actionLinkSx}
            disabled={!props.aws || !newRoleName || !newExternalId}
            endIcon={<OutboundIcon sx={{ fontSize: 16 }} />}
            onClick={() => window.open(getPairingConsoleUrl(), "_blank")}
          >
            1 Account Pairing
          </Button>
          <Button
            variant="outlined"
            sx={guideBtnSx}
            disabled={props.isReleaseDetailsGetting}
            startIcon={<DocumentIcon sx={{ fontSize: 16 }} />}
            onClick={handleManualPairingGuide}
          >
            Guide
          </Button>

          <Button
            variant="text"
            sx={actionLinkSx}
            endIcon={<OutboundIcon sx={{ fontSize: 16 }} />}
            onClick={() => window.open(RESOURCE_EXPLORER_URL, "_blank")}
          >
            2 Enable Resource Explorer
          </Button>
          <Button
            variant="outlined"
            sx={guideBtnSx}
            disabled={props.isReleaseDetailsGetting}
            startIcon={<DocumentIcon sx={{ fontSize: 16 }} />}
            onClick={handleResourceExplorerGuide}
          >
            Guide
          </Button>
        </Box>

        <Box sx={{ mb: 1.25 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#64748b",
            }}
          >
            IAM Role
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="newRoleName"
            value={newRoleName}
            placeholder="Role Name"
            type="text"
            onChange={(e) => setNewRoleName(e.target.value)}
            error={!newRoleName}
            helperText={!newRoleName && "IAM Role is required"}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: "8px",
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#64748b",
            }}
          >
            External ID
          </Typography>
          <TextField
            fullWidth
            size="small"
            name="newExternalId"
            value={newExternalId}
            placeholder="External ID"
            type="text"
            disabled={true}
            error={!newExternalId}
            helperText={!newExternalId && "External Id is required"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy} size="small">
                    {isCopied ? (
                      <Typography sx={{ fontSize: "0.75rem", color: "#2563eb" }}>
                        Copied
                      </Typography>
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    )}
                  </IconButton>
                  <IconButton onClick={handleRefresh} size="small">
                    <RefreshIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: "8px",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="text"
            sx={{ ...actionLinkSx, fontSize: "0.8125rem", py: 0 }}
            onClick={handleClicktoCopy}
          >
            Copy Pairing Link
          </Button>
          {/* Keep ref available for any legacy consumers */}
          <textarea
            readOnly
            ref={linkTextAreaRef}
            value={getPairingConsoleUrl()}
            style={{ display: "none" }}
            aria-hidden
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2.5,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Button
          variant="contained"
          disabled={
            props.awsAccountPosting ||
            isInvalid() ||
            props.accountStatusLoading ||
            props.accountStatusError
          }
          onClick={handleUpdateIamRole}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            borderRadius: "8px",
            px: 3,
            py: 1,
            backgroundColor: "#F98125",
            color: "#fff",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#e8741c",
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: "#cbd5e1",
              color: "#fff",
            },
          }}
        >
          {props?.selectedAccount?.status === "ACTIVE"
            ? "Update Role and External ID"
            : "Activate"}
        </Button>
      </Box>
      {!props.onOpenGuidePreview && (
        <RemediationPreviewPanel
          url={localGuidePreviewUrl}
          onClose={() => setLocalGuidePreviewUrl(null)}
          title="Guide"
        />
      )}
    </Box>
  );
};

const mapStateToProps = (state) => {
  const { awsAccountPosting, postAwsAccMessage, postAwsAccError } =
    state.awsAcc;

  const { aws } = state.awsAcc;

  const {
    isReleaseDetailsGetting,
    getReleaseDetailsMessage,
    getReleaseDetailsError,
    releaseDetails,
  } = state.releaseNotes;

  const { accountStatusLoading, accountStatusResult, accountStatusError } =
    state.scanService;

  return {
    awsAccountPosting,
    postAwsAccMessage,
    postAwsAccError,

    isReleaseDetailsGetting,
    getReleaseDetailsMessage,
    getReleaseDetailsError,
    releaseDetails,

    aws,

    accountStatusLoading,
    accountStatusResult,
    accountStatusError,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    postAwsAcc: (awsAccountUpdateRequest) => {
      dispatch(postAwsAcc(awsAccountUpdateRequest));
    },
    getListOfAccts: () => {
      dispatch(getListOfAccts());
    },
    getReleaseDetails: (fileName) => {
      dispatch(getReleaseDetailsRequest(fileName));
    },
    clearAllReleaseMessages: () => {
      dispatch(clearAllReleaseMessages());
    },
    getAwsAcc: () => {
      dispatch(getAwsAcc());
    },
    clearAllScanServiceMessages: () => {
      dispatch(clearAllScanServiceMessages());
    },
  };
};

const UpdateRoleInfo = connect(
  mapStateToProps,
  mapDispatchToProps
)(UpdateRoleInfoComp);

const UpdateIamRole = (props) => {
  console.log("UpdateIamRole, props", props);
  console.log("UpdateIamRole, props.selectedAccount", props.selectedAccount);
  console.log("UpdateIamRole, props.externalId", props.externalId);
  const [tabValue, setTabValue] = useState(0);
  const [updateRoleDone, setUpdateRoleDone] = useState(false);
  const [newRoleName, setNewRoleName] = useState(
    props.selectedAccount?.iamrole || "CloudcatcherAuditTrustRole"
  );
  const [newExternalId, setNewExternalId] = useState(props?.externalId);
  const [accountStatus, setAccountStatus] = useState(
    props.selectedAccount?.status
  );

  const handleUpdateIamRole = () => {
    const updateRoleRequest = {
      accountNumber: props.selectedAccount.accountNumber,
      iamRole: newRoleName,
      externalId: newExternalId,
    };
    console.log("updateRoleRequest: ", updateRoleRequest);
    props.postAwsAcc(updateRoleRequest);
  };

  const accountStatusResult = props.accountStatusResult;
  const accountStatusError = props.accountStatusError;
  useEffect(() => {
    if (accountStatusResult) {
      const updateRoleRequest = {
        accountNumber: props.selectedAccount.accountNumber,
        iamRole: newRoleName,
        externalId: newExternalId,
      };
      console.log("updateRoleRequest: ", updateRoleRequest);
      props.postAwsAcc(updateRoleRequest);
    }
    if (accountStatusError) {
      enqueueSnackbar(accountStatusError, { variant: "error" });
    }
    // props.clearAllScanServiceMessages();
  }, [accountStatusResult, accountStatusError]);

  const postAwsAccMessage = props.postAwsAccMessage;
  const getListOfAccts = useCallback(() => {
    props.getListOfAccts();
  }, []);
  const closeModal = useCallback(() => {
    // clear scan service messages
    props.clearAllScanServiceMessages();
    props.closeModal();
  }, []);
  useEffect(() => {
    console.log("UpdateIamRole: useEffect");
    if (postAwsAccMessage) {
      if (accountStatus === "INACTIVE") {
        setUpdateRoleDone(true);
        setTabValue(1);
        setAccountStatus("ACTIVATED");
      } else if (accountStatus === "ACTIVE") {
        closeModal();
      }
      if (accountStatus !== "ACTIVATED") {
        getListOfAccts();
      }
    }
  }, [
    postAwsAccMessage,
    getListOfAccts,
    closeModal,
    newExternalId,
    newRoleName,
    accountStatus,
  ]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <SnackbarProvider />
      {/* {props?.selectedAccount?.status === "ACTIVE" ? ( */}
      {accountStatus === "ACTIVE" ? (
        <UpdateRoleInfo
          handleUpdateIamRole={handleUpdateIamRole}
          selectedAccount={props.selectedAccount}
          newRoleName={newRoleName}
          setNewRoleName={setNewRoleName}
          newExternalId={newExternalId}
          setNewExternalId={setNewExternalId}
          closeModal={closeModal}
          onOpenGuidePreview={props.onOpenGuidePreview}
        />
      ) : (
        <div>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="IAM Role Update" />
            <Tab label="Addons" disabled={!updateRoleDone} />
          </Tabs>
          {tabValue === 0 && (
            <UpdateRoleInfo
              handleUpdateIamRole={handleUpdateIamRole}
              selectedAccount={props.selectedAccount}
              newRoleName={newRoleName}
              setNewRoleName={setNewRoleName}
              newExternalId={newExternalId}
              setNewExternalId={setNewExternalId}
              closeModal={closeModal}
              onOpenGuidePreview={props.onOpenGuidePreview}
            />
          )}
          {tabValue === 1 && (
            <div
              style={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <AdditionalAddons
                addOns={props.addOns}
                editAccount={props.selectedAccount}
                onOpenGuidePreview={props.onOpenGuidePreview}
              />
              <footer
                style={{
                  marginTop: "auto",
                  padding: "1rem",
                  display: "flex",
                  justifyContent: "flex-end",
                  borderTop: "1px solid #ddd",
                }}
              >
                <button
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={closeModal} // Replace with your close modal function
                >
                  Close
                </button>
              </footer>
            </div>
          )}
        </div>
      )}
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateIamRole);
